import { ValidationError } from 'yup';
import type { StringSchema } from 'yup';
import { validateYupSchema, yupToFormErrors } from 'formik';
import type { FormikErrors, FormikValues } from 'formik';
import { CamundaFields } from '@forvalter/utils/CamundaUtils';
import { ValidFormRegions } from '@klp/formgenerator/interfaces';
import type { FORM_REGION } from '@klp/formgenerator/interfaces';

export const isValidFormRegion = (region: string): region is FORM_REGION => {
    return (ValidFormRegions as ReadonlyArray<string>).includes(region);
};

/**
 * Validates organization numbers based on region
 */
export const handleOrgnummerValidering = (
    stringInput: string,
    region?: FORM_REGION,
): boolean => {
    switch (region) {
        case 'Sverige':
            return /^\d{10}$/g.test(stringInput);
        case 'Europa':
            return /^\d{8}$/g.test(stringInput);
        default:
            return /^\d{9}$/g.test(stringInput);
    }
};

/**
 * Validates if a JSON string represents a valid building object
 */
export const isValidBuildingJson = (jsonString?: string): boolean => {
    if (!jsonString) return false;

    try {
        const data = JSON.parse(jsonString) as { id: string; navn: string };

        return Boolean(data.id && data.navn);
    } catch {
        return false;
    }
};

/**
 * Camunda stores string process variables in a 4000 character column and
 * rejects longer values with "Variable value is too long".
 */
export const TEXTAREA_MAX_LENGTH = 4000;

export const exceedsTextAreaMaxLength = (value?: string): boolean =>
    (value?.length ?? 0) > TEXTAREA_MAX_LENGTH;

// Subtypes rendered as free-text inputs: textarea, BasicTextField (no subtype)
// and conditional comment fields such as garantiKommentar
const FREE_TEXT_SUBTYPES: ReadonlyArray<string | undefined> = [
    undefined,
    '',
    CamundaFields.TEXTAREA,
    CamundaFields.CONDITIONAL,
];

export const isFreeTextSubtype = (subtype?: string): boolean =>
    FREE_TEXT_SUBTYPES.includes(subtype);

export const withTextMaxLength = (
    validator: StringSchema,
    subtype: string | undefined,
    t?: (key: string, options?: Record<string, unknown>) => string,
): StringSchema => {
    if (!isFreeTextSubtype(subtype)) return validator;

    return validator.max(
        TEXTAREA_MAX_LENGTH,
        t?.('formGenerator.maxLengthExceeded', { max: TEXTAREA_MAX_LENGTH }) ??
            `The text cannot be longer than ${TEXTAREA_MAX_LENGTH} characters`,
    );
};

/**
 * Validates a values snapshot synchronously and returns Formik-shaped errors.
 * Uses Formik's own preparation (empty strings become undefined), so results
 * match what the form reports on submit.
 */
export const validateValuesSync = <Values extends FormikValues>(
    values: Values,
    schema: unknown,
): FormikErrors<Values> => {
    try {
        validateYupSchema(values, schema, true);
        return {};
    } catch (error) {
        if (ValidationError.isError(error)) {
            return yupToFormErrors<Values>(error);
        }
        throw error;
    }
};
